// Public survey at /health/:token (no auth; the invitation token is the
// capability). Handoff Section 7: privacy screen before the first question,
// a 14-section stepper plus review, server-side autosave, return-and-edit
// while the cycle is open, read-only after close. The tone throughout is
// pastoral and plain: this is an invitation to honest reflection, not an
// audit or a ranking.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PortalHeader } from '@cca/ui';
import { RegistrarContact } from '../../access/RegistrarContact';
import {
  healthApi,
  HealthApiError,
  type InstrumentSection,
  type PublicInstrumentResponse,
} from '../api';
import { formatNzDate, roleLabel, ROLE_ORDER } from '../format';
import { partitionFollowUps, renderInstrumentText } from '../markup';
import { SectionStep } from './SectionStep';
import { TurnstileWidget, type TurnstileFailure } from './TurnstileWidget';
import '../health.css';

type Phase =
  | 'loading'
  | 'invalid'
  | 'busy'
  | 'module_closed'
  | 'decline_confirm'
  | 'declined'
  | 'privacy'
  | 'survey'
  | 'submitted'
  | 'edit_locked'
  | 'not_open_yet'
  | 'closed';

/** Answer state kept locally. A null score means "this answer was cleared":
 *  drafts must carry the null so the server deletes the saved answer rather
 *  than resurrecting it on merge; submits omit cleared answers entirely. */
type ScoreMap = Record<string, number | null>;

/** The "Just checking" line's two wordings (cca-b2vom): while the bot check
 *  runs, and once its token is in ("Browser check done.", the registrar's
 *  words, 4 Nov). The line shows one (none while a failure notice shows) and
 *  carries both in data attributes, from which its CSS reserves the height of
 *  the taller (health.css). */
const CHECK_LINE = {
  checking: 'Just checking your browser. If a prompt appears above, please complete it.',
  done: 'Browser check done.',
} as const;

export function SurveyPage(): JSX.Element {
  const { token: urlToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<PublicInstrumentResponse | null>(null);
  const [personalToken, setPersonalToken] = useState<string>('');
  const [scores, setScores] = useState<ScoreMap>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [role, setRole] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [bookmarkNotice, setBookmarkNotice] = useState(false);
  const [welcomeBack, setWelcomeBack] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Words, or words plus the registrar's address (the refused bot check).
  const [submitError, setSubmitError] = useState<ReactNode>(null);
  const [declining, setDeclining] = useState(false);
  // Edit re-auth: once verified (or after a submit this session) editing stays
  // open for the server's unlock window, so the gate is not shown again.
  const [editUnlocked, setEditUnlocked] = useState(false);
  // Turnstile: token from the widget (single-use); the nonce remounts the
  // widget to mint a fresh token after a spent/failed attempt, or when the
  // respondent presses "Try the check again". The failure is what the widget
  // reported when it cannot give a token by waiting (cca-2s7tg): the review
  // step says so, keeps the answers, and names who can help.
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileFailure, setTurnstileFailure] = useState<TurnstileFailure | null>(null);
  // Pressing "Try the check again" unmounts it with the notice that holds it,
  // so focus would fall to <body>: instead it follows the check (the effect
  // below), waiting on the "Just checking" line while the check runs and
  // moving on when a token or a failure comes. Counted apart from the nonce,
  // which a refused submit also bumps: after a retry the line shows even with
  // nothing answered yet, so focus has somewhere to wait.
  const [checkRetries, setCheckRetries] = useState(0);
  const checkingRef = useRef<HTMLParagraphElement>(null);
  // One-shot: set by the person's retry and by focus entering the notice;
  // acted on, and cleared, when the check next changes state. Kept while the
  // notice only changes its words in place with focus still inside it.
  const followCheckRef = useRef(false);
  const noticeRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const firstRowRef = useRef<HTMLButtonElement>(null);
  // The review step's "needs at least one answer" hint. While nothing is
  // answered the first section row is described by it, so focus that falls
  // back to that row also says why it landed there.
  const nothingAnsweredHintId = useId();
  const refusalRef = useRef<HTMLDivElement>(null);
  // Moving to another step moves focus to that step's heading (cca-iwefb,
  // WCAG 2.2 SC 2.4.3), as NewCyclePage's step 2 does. The button pressed
  // stays where it was and React reuses it for the next step: Next stays at
  // the foot of a page scrolled to its top, and Review becomes Submit,
  // disabled until the bot check passes, which cannot hold focus, so focus
  // fell to <body> (Miranda O1). Counted per press, so every press asks once;
  // zero on load, so arriving at the page moves no focus.
  const [stepMoves, setStepMoves] = useState(0);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  // Shared across sections: expand the 1-4 explanation once and it stays
  // expanded on every section as you move through the survey.
  const [scaleHelpOpen, setScaleHelpOpen] = useState(false);
  const loadedRef = useRef(false);
  // Autosave/submit race guards: once a submit starts, no draft save may fire
  // or land, or a stale draft would resurrect after the server clears it.
  const submitStartedRef = useRef(false);
  const draftTimerRef = useRef<number | null>(null);
  const draftAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!urlToken) {
      setPhase('invalid');
      return;
    }
    let cancelled = false;
    healthApi
      .publicInstrument(urlToken)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setPersonalToken(res.token);
        setScores(res.answers.scores);
        setTexts(res.answers.texts);
        setRole(res.invitation.role);

        // Open-link minting: the URL held the shared cycle token; the server
        // minted a personal one. Put the personal link in the address bar and
        // ask the respondent to bookmark it.
        if (res.token !== urlToken) {
          window.history.replaceState(null, '', `/health/${res.token}${window.location.search}`);
          setBookmarkNotice(true);
        }

        if (searchParams.get('decline') === '1') {
          setPhase('decline_confirm');
        } else if (res.cycle.not_yet_open) {
          setPhase('not_open_yet');
        } else if (!res.cycle.is_open) {
          setPhase('closed');
        } else if (res.invitation.first_visit) {
          setPhase('privacy');
        } else if (res.invitation.edit_reauth_required) {
          // Already submitted, and this cycle asks for a one-time code before
          // changes can be made.
          setPhase('edit_locked');
        } else {
          setWelcomeBack(true);
          setPhase('survey');
        }
        // Autosave only reacts to changes made after this point.
        window.setTimeout(() => {
          loadedRef.current = true;
        }, 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof HealthApiError && err.status === 410) {
          setPhase('module_closed');
        } else if (err instanceof HealthApiError && err.status === 429) {
          setPhase('busy');
        } else {
          setPhase('invalid');
        }
      });
    return () => {
      cancelled = true;
    };
    // Load once per token; searchParams is read at load time only.
  }, [urlToken]);

  // ── Autosave (server-side draft, ~1.5s after the last change) ─────────────
  // Cleared answers stay in the payload as explicit nulls (scores) or empty
  // strings (texts) so the worker's merge deletes them instead of leaving the
  // previously saved answer to resurrect.
  const saveDraft = useCallback(async () => {
    if (!personalToken || submitStartedRef.current) return;
    const controller = new AbortController();
    draftAbortRef.current = controller;
    setSaveState('saving');
    try {
      await healthApi.publicDraft(
        personalToken,
        {
          scores,
          texts,
          ...(role ? { role } : {}),
        },
        controller.signal,
      );
      if (submitStartedRef.current) return;
      setSaveState('saved');
    } catch {
      // Quiet failure: the submit path is the one that must not lose answers.
      if (!submitStartedRef.current) setSaveState('idle');
    }
  }, [personalToken, scores, texts, role]);

  useEffect(() => {
    if (!loadedRef.current) return;
    if (phase !== 'survey' && phase !== 'privacy') return;
    if (submitStartedRef.current) return;
    const timer = window.setTimeout(() => {
      void saveDraft();
    }, 1500);
    draftTimerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (draftTimerRef.current === timer) draftTimerRef.current = null;
    };
  }, [scores, texts, role, phase, saveDraft]);

  // Focus follows the check's next outcome once the person has retried it or
  // moved focus into its notice (Miranda r1 B1). The notice that holds "Try
  // the check again" unmounts on most outcomes, which would leave focus on
  // <body>, and on a failure the "Just checking" line falls silent, which
  // would leave focus on a node with nothing to say. While the flag is set:
  // the line while the check runs (the flag stays set for its outcome), which
  // is also where focus goes when a challenge appears and withdraws the
  // notice; the first section row instead when there is no line (nothing
  // answered and no retry yet); the new notice's retry button if it fails
  // again; Submit when a token arrives, or the first section row while Submit
  // stays disabled (nothing answered), since a disabled button cannot hold
  // focus. The line stays for the token, its words changed to "Browser check
  // done.", but focus waiting on it still moves on: the token is what it was
  // waiting for (cca-b2vom). A notice that changes its words in place
  // ("taking longer" to "could not finish") stays mounted with focus inside
  // it: focus stays where it is and the flag is kept for the outcome after
  // (cca-ofmfo). It acts only while focus is on <body>, on the line, on the
  // step's heading or inside the notice, so focus the person moved elsewhere
  // stays put. The heading counts as <body> does: the page put focus there on
  // arriving at the review step (cca-iwefb), and a press that moves no focus
  // leaves it there. A first, unprompted failure sets no flag and moves
  // nothing: its role="alert" carries it.
  useEffect(() => {
    if (!followCheckRef.current) return;
    const active = document.activeElement;
    const line = checkingRef.current;
    const heading = stepHeadingRef.current;
    const inNotice = active !== null && noticeRef.current?.contains(active) === true;
    if (
      active !== null &&
      active !== document.body &&
      active !== line &&
      active !== heading &&
      !inNotice
    ) {
      followCheckRef.current = false;
      return;
    }
    if (turnstileFailure) {
      // Still inside the notice: its words changed in place and focus was
      // never lost, so it stays where it is (the retry button or the
      // registrar's link). The flag is kept, not cleared: focus() on an
      // element that already has focus fires no event, so the notice's
      // onFocus would not set it again.
      if (inNotice) return;
      // Cleared first: focusing the button enters the new notice, whose
      // onFocus sets the flag again, for a late token or challenge.
      followCheckRef.current = false;
      retryRef.current?.focus();
    } else if (turnstileToken) {
      followCheckRef.current = false;
      const submitButton = submitRef.current;
      if (submitButton && !submitButton.disabled) submitButton.focus();
      else firstRowRef.current?.focus();
    } else if (line) {
      line.focus();
    } else {
      // No line means nothing is answered: the row is then described by the
      // "needs at least one answer" hint, which says why focus is there.
      followCheckRef.current = false;
      firstRowRef.current?.focus();
    }
  }, [turnstileToken, turnstileFailure]);

  // A refused submit moves focus to the banner that says so (Miranda r1 S1),
  // as NewCyclePage's refused launch does: otherwise it is left on <body>.
  // Declared after the effect above, so the refusal wins if both act.
  useEffect(() => {
    if (submitError) refusalRef.current?.focus();
  }, [submitError]);

  // A step change moves focus to the new step's heading (stepMoves, above).
  // It runs after the press's render, so the heading is the new step's, and
  // after the press scrolled the page to its top (moveToStep, below). So
  // focus() comes last and scrolls only if the heading is still out of view:
  // it ends both focused and in view.
  useEffect(() => {
    if (stepMoves > 0) stepHeadingRef.current?.focus();
  }, [stepMoves]);

  // ── Renders per phase ──────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <Shell>
        <p style={{ color: 'var(--health-grey)' }}>Loading your assessment...</p>
      </Shell>
    );
  }

  if (phase === 'invalid') {
    return (
      <Shell>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>This link is not valid</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            The link you followed does not match a current assessment. It may have been copied
            incompletely, or it may belong to an assessment that has been removed. If you were
            invited by email, please open the link from that email again, or reply to it and we will
            help.
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'busy') {
    return (
      <Shell>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>The assessment is busy right now</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            Quite a few people are opening the assessment at the moment. Nothing is wrong with your
            link, and nothing you have saved is lost. Please try again in a few minutes.
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'module_closed') {
    return (
      <Shell>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>The assessment is not open right now</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            Thank you for coming. The Church Health Assessment is not accepting responses at the
            moment. If you believe this is a mistake, please reply to your invitation email and we
            will look into it.
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <p style={{ color: 'var(--health-grey)' }}>Something went wrong loading the assessment.</p>
      </Shell>
    );
  }

  const { instrument, cycle } = data;
  const sections = instrument.sections;
  const reviewStep = sections.length;
  const totalAnswered =
    Object.values(scores).filter((v) => v !== null).length +
    Object.values(texts).filter((t) => t.trim().length > 0).length;

  if (phase === 'decline_confirm') {
    return (
      <Shell churchName={cycle.church_name}>
        <div className="card">
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>Would you rather not take part?</h2>
          <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
            That is entirely alright. If you confirm below, we will not send you any further emails
            about this assessment for {cycle.church_name}.
            {cycle.is_open
              ? ' If you change your mind before the assessment closes, this link will still let you take part.'
              : ' The assessment has now closed, so no answers can be given either way.'}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={declining}
              onClick={() => {
                setDeclining(true);
                healthApi
                  .publicDecline(personalToken)
                  .then(() => setPhase('declined'))
                  .catch(() => setPhase('declined'))
                  .finally(() => setDeclining(false));
              }}
            >
              {declining ? 'One moment...' : 'Confirm, I would rather not take part'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                window.history.replaceState(null, '', `/health/${personalToken}`);
                // A closed cycle must never open as an editable survey, even
                // from the decline screen.
                if (!cycle.is_open) {
                  setPhase('closed');
                } else {
                  setPhase(data.invitation.first_visit ? 'privacy' : 'survey');
                }
              }}
            >
              {cycle.is_open
                ? 'Actually, I would like to take part'
                : 'Show me the assessment instead'}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  if (phase === 'declined') {
    return (
      <Shell churchName={cycle.church_name}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>Thank you for letting us know</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            You will not be emailed about this again.
            {cycle.is_open &&
              ' If you change your mind while the assessment is still open, this same link will let you take part.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'not_open_yet') {
    return (
      <Shell churchName={cycle.church_name}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>This assessment has not opened yet</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            Thank you for coming early. The assessment for {cycle.church_name} opens on{' '}
            {cycle.opens_at ? formatNzDate(cycle.opens_at) : 'its opening date'}
            {cycle.closes_at ? ` and stays open until ${formatNzDate(cycle.closes_at)}` : ''}. This
            same link will work from then, so please keep it and come back.
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'closed') {
    return (
      <Shell churchName={cycle.church_name}>
        <div className="health-banner" role="status" style={{ marginBottom: 16 }}>
          This assessment has now closed. Your answers are shown below exactly as you left them, and
          they can no longer be changed. Thank you for taking part.
        </div>
        <ReadOnlyAnswers
          sections={sections}
          scores={scores}
          texts={texts}
          scale={instrument.scale.labels}
        />
      </Shell>
    );
  }

  if (phase === 'privacy') {
    const openMode = cycle.mode === 'open';
    const needsRole = openMode && !role;
    return (
      <Shell churchName={cycle.church_name}>
        {bookmarkNotice && <BookmarkNotice />}
        <div className="card">
          <h2 style={{ fontSize: 22, marginBottom: 6 }}>Church Health Assessment</h2>
          <p style={{ fontSize: 14, color: 'var(--health-grey)', marginBottom: 16 }}>
            {cycle.church_name}
          </p>
          <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 14 }}>
            Thank you for being willing to reflect honestly on the life of your church. This is an
            invitation to honest reflection before God, not an audit and not a ranking. Most
            responders take 20 to 30 minutes; you can save part way and return to this link whenever
            it suits. Before you begin, here is exactly how your answers will be handled:
          </p>
          <ul
            style={{
              fontSize: 14,
              lineHeight: 1.7,
              paddingLeft: 22,
              marginBottom: 16,
              display: 'grid',
              gap: 8,
            }}
          >
            <li>
              Your individual answers are only ever reported anonymously to the facilitator and your
              church&apos;s leaders. No one will be shown which answers are yours.
            </li>
            <li>
              You may return to this link to change your answers at any time until the assessment
              closes.
            </li>
            <li>
              Written answers are shared both word for word and summarised to draw out common
              themes. Be assured, in either form, your name will not be attached.
            </li>
            {data.invitation.has_email && (
              <li>We hold your email address only to send you this link and reminders.</li>
            )}
          </ul>
          {openMode && (
            <RolePicker
              roles={data.roles}
              value={role}
              onChange={setRole}
              heading="Before you begin, which of these best describes your part in the life of the church?"
            />
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 8 }}
            disabled={needsRole}
            onClick={() => setPhase('survey')}
          >
            Begin
          </button>
          {needsRole && (
            <p style={{ fontSize: 13, color: 'var(--health-grey)', marginTop: 8 }}>
              Choose the role that best describes you.
            </p>
          )}
        </div>
      </Shell>
    );
  }

  if (phase === 'submitted') {
    return (
      <Shell churchName={cycle.church_name}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>Thank you</h2>
          <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
            Your reflections have been received. They will only ever be reported anonymously,
            alongside those of the other responders taking part.
          </p>
          <p
            style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--health-grey)', marginBottom: 16 }}
          >
            You can return to this link and change your answers any time until the assessment closes
            {cycle.closes_at ? ` on ${formatNzDate(cycle.closes_at)}` : ''}.
            {!data.invitation.has_email &&
              ' Please bookmark this page: this link is the only way back to your answers.'}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                submitStartedRef.current = false;
                setStep(0);
                // If this cycle re-authenticates edits and the window has
                // lapsed, ask for a code before reopening the survey.
                if (data.invitation.edit_reauth_required && !editUnlocked) {
                  setPhase('edit_locked');
                } else {
                  setPhase('survey');
                }
              }}
            >
              Edit my answers
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                // Browsers often refuse to close a tab the person opened
                // themselves; the caption below keeps the state clear anyway.
                window.close();
              }}
            >
              Close this page
            </button>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 12 }}>
            Your answers are saved. You can safely close this tab.
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'edit_locked') {
    return (
      <Shell churchName={cycle.church_name}>
        <EditLockGate
          token={personalToken}
          churchName={cycle.church_name}
          onUnlock={() => {
            setEditUnlocked(true);
            setWelcomeBack(true);
            setStep(0);
            submitStartedRef.current = false;
            setPhase('survey');
          }}
          onCancel={() => setPhase('submitted')}
        />
      </Shell>
    );
  }

  // ── phase === 'survey': the stepper ────────────────────────────────────────
  const isReview = step === reviewStep;
  const section = isReview ? null : sections[step];
  // A returning respondent has submitted before, and re-submits.
  const returning = data.invitation.submitted;
  // The submit button's resting label, also named in the refused bot check's
  // words, so those words name the button the person actually sees.
  const submitLabel = returning ? 'Re-submit my answers' : 'Submit my answers';

  /** Every step change the person makes (Next, Review, Back, a review row):
   *  to that step, the page to its top, and then focus to the step's heading
   *  (the effect on stepMoves). */
  const moveToStep = (next: SetStateAction<number>): void => {
    setStep(next);
    setStepMoves((n) => n + 1);
    window.scrollTo(0, 0);
  };

  const submit = (): void => {
    // Stop the autosave first: cancel any pending debounce, abort any draft
    // PUT still in flight, and flag the guard so a late timer cannot fire a
    // stale draft after the server has cleared it.
    submitStartedRef.current = true;
    if (draftTimerRef.current !== null) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    draftAbortRef.current?.abort();

    // Submit payloads omit cleared answers entirely (no nulls, no empties).
    const submitScores: Record<string, number> = {};
    for (const [id, value] of Object.entries(scores)) {
      if (typeof value === 'number') submitScores[id] = value;
    }
    const submitTexts: Record<string, string> = {};
    for (const [id, value] of Object.entries(texts)) {
      if (value.trim().length > 0) submitTexts[id] = value;
    }

    setSubmitting(true);
    setSubmitError(null);
    healthApi
      .publicSubmit(personalToken, {
        scores: submitScores,
        texts: submitTexts,
        ...(role ? { role } : {}),
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      })
      .then(() => {
        // A successful submit opens the server's edit window, so the gate is
        // not shown again this session.
        setEditUnlocked(true);
        setPhase('submitted');
        window.scrollTo(0, 0);
      })
      .catch((err: unknown) => {
        // The submit did not land, so autosave may resume. A Turnstile token is
        // single-use, so mint a fresh one for the retry.
        submitStartedRef.current = false;
        setTurnstileToken('');
        setTurnstileNonce((n) => n + 1);
        if (err instanceof HealthApiError && err.code === 'closed') {
          setPhase('closed');
        } else if (err instanceof HealthApiError && err.code === 'pin_required') {
          // The edit window lapsed mid-session; step back to the code gate.
          setEditUnlocked(false);
          setPhase('edit_locked');
        } else if (err instanceof HealthApiError && err.code === 'turnstile_failed') {
          // UX-6: the refusal names who can help (cca-2s7tg). It inherits the
          // banner's own text colour, as RegistrarContact asks inside a tint.
          // The page has already started a fresh check (the nonce above), so
          // the words say so rather than ask the person to redo it. They name
          // the button by its own label: "Re-submit" for a returning respondent,
          // whose earlier submission still stands (the door refuses before it
          // writes anything), so only their changes went unsent.
          setSubmitError(
            <>
              <p>
                {returning
                  ? 'The browser check did not go through, so your changes were not sent; they are still here, and the answers you submitted before still stand.'
                  : 'The browser check did not go through, so your answers were not sent. They are still here.'}{' '}
                A fresh check has started; when &apos;{submitLabel}&apos; is ready, press it again.
              </p>
              <RegistrarContact className="mt-1" />
            </>,
          );
        } else if (err instanceof HealthApiError && err.code === 'empty_submission') {
          setSubmitError('Please answer at least one question before submitting.');
        } else {
          setSubmitError(
            'Your answers could not be submitted just now. They are saved as a draft, so nothing is lost. Please try again in a moment.',
          );
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Shell churchName={cycle.church_name}>
      {bookmarkNotice && <BookmarkNotice />}
      {welcomeBack && (
        <div
          className="health-banner health-banner-blue"
          role="status"
          style={{ marginBottom: 16 }}
        >
          Welcome back. You can change any answer and re-submit at any time before the assessment
          closes{cycle.closes_at ? ` on ${formatNzDate(cycle.closes_at)}` : ''}.
        </div>
      )}

      {/* Progress */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--health-grey)' }}>
            {isReview
              ? `Review and submit (step ${reviewStep + 1} of ${reviewStep + 1})`
              : `Section ${step + 1} of ${sections.length}`}
          </span>
          <span style={{ fontSize: 12, color: 'var(--health-grey)' }} aria-live="polite">
            {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : ''}
          </span>
        </div>
        <div className="health-progress-track">
          <div
            className="health-progress-fill"
            style={{ width: `${Math.round(((step + 1) / (reviewStep + 1)) * 100)}%` }}
          />
        </div>
      </div>

      {section && (
        <SectionStep
          section={section}
          scale={instrument.scale.labels}
          scores={scores}
          texts={texts}
          onScore={(id, value) =>
            // A cleared score stays in the map as null so the next draft save
            // tells the server to delete it rather than leave it to resurrect.
            setScores((prev) => ({ ...prev, [id]: value ?? null }))
          }
          onText={(id, value) => setTexts((prev) => ({ ...prev, [id]: value }))}
          scaleHelpOpen={scaleHelpOpen}
          onScaleHelpToggle={setScaleHelpOpen}
          headingRef={stepHeadingRef}
        />
      )}

      {isReview && (
        <ReviewStep
          sections={sections}
          scores={scores}
          texts={texts}
          goTo={(i) => {
            // Leaving the review step ends any focus that follows the check.
            followCheckRef.current = false;
            moveToStep(i);
          }}
          headingRef={stepHeadingRef}
          firstRowRef={firstRowRef}
          firstRowDescribedBy={totalAnswered === 0 ? nothingAnsweredHintId : undefined}
          rolePicker={
            cycle.mode === 'open' ? (
              <RolePicker
                roles={data.roles}
                value={role}
                onChange={setRole}
                heading="Which of these best describes your part in the life of the church?"
              />
            ) : null
          }
        />
      )}

      {isReview && data.turnstile_site_key && (
        <div style={{ marginTop: 16 }}>
          <TurnstileWidget
            key={turnstileNonce}
            siteKey={data.turnstile_site_key}
            onToken={(t) => {
              // A token, or none yet with nothing wrong: either way an earlier
              // failure is over.
              setTurnstileToken(t);
              setTurnstileFailure(null);
            }}
            onFailure={(kind) => {
              setTurnstileToken('');
              setTurnstileFailure(kind);
            }}
          />
          {/* Below the widget it may point at, above Submit, in the slot the
              "Just checking" line takes when nothing has failed (the two never
              show together: while this shows, the line after it has no words
              and no height). Submit stays disabled: the door refuses a
              submission without a token. */}
          {turnstileFailure && (
            <BotCheckNotice
              failure={turnstileFailure}
              noticeRef={noticeRef}
              retryRef={retryRef}
              onFocusWithin={() => {
                followCheckRef.current = true;
              }}
              onRetry={() => {
                // Remake the widget (a fresh script load after a failed one).
                // The answers stay in this page's state; nothing reloads.
                followCheckRef.current = true;
                setTurnstileFailure(null);
                setTurnstileToken('');
                setTurnstileNonce((n) => n + 1);
                setCheckRetries((n) => n + 1);
              }}
            />
          )}
          {/* A polite live region, and where focus waits while a retried check
              runs (followCheckRef), so it shows after a retry (checkRetries)
              even with nothing answered yet. It sits straight under the widget
              whose prompt it points at, so focus moved here has no Back or
              Submit between it and that prompt. Once shown it stays, the same
              node, while this step shows, and only its words change, in
              place, so every change is announced: "Just checking" while the
              check runs, "Browser check done." once a token is in, and no
              words at all while a failure notice shows (cca-4o91n, Miranda
              S1). Kept, silent, after the notice, it is already on the page
              when a late token withdraws the notice, so its "Browser check
              done." answers the notice's alert; a region mounted already
              saying it may not be read. Silent, it carries neither its class
              nor its data attributes, so it takes no height and the notice
              keeps the slot under the widget. With nothing answered and no
              retry it never shows, so it is not there for a late token
              either. Back and Submit below it do not move as a token comes
              and goes (Miranda cca-b2vom r1 J1): the data attributes carry
              both wordings to its CSS, which reserves the height of the
              taller. Its class carries the focus ring. */}
          {(totalAnswered > 0 || checkRetries > 0) && (
            <p
              ref={checkingRef}
              role="status"
              tabIndex={-1}
              className={turnstileFailure ? undefined : 'health-checking-line'}
              data-words-checking={turnstileFailure ? undefined : CHECK_LINE.checking}
              data-words-done={turnstileFailure ? undefined : CHECK_LINE.done}
            >
              <span>
                {turnstileFailure ? '' : turnstileToken ? CHECK_LINE.done : CHECK_LINE.checking}
              </span>
            </p>
          )}
        </div>
      )}

      {/* Focused when it appears (refusalRef), so it wears the module's focus
          ring; role="alert" announces it as well. */}
      {submitError && (
        <div
          ref={refusalRef}
          tabIndex={-1}
          className="health-banner health-banner-red health-submit-refusal"
          role="alert"
          style={{ marginTop: 16 }}
        >
          {submitError}
        </div>
      )}

      {/* Step navigation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 20,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          className="btn btn-ghost"
          disabled={step === 0}
          onClick={() => {
            // As the review rows' goTo: leaving review ends the focus follow.
            followCheckRef.current = false;
            moveToStep((s) => Math.max(0, s - 1));
          }}
        >
          Back
        </button>
        {isReview ? (
          <button
            ref={submitRef}
            type="button"
            className="btn btn-primary"
            disabled={
              submitting ||
              totalAnswered === 0 ||
              (Boolean(data.turnstile_site_key) && !turnstileToken)
            }
            onClick={submit}
          >
            {submitting ? 'Submitting...' : submitLabel}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => moveToStep((s) => Math.min(reviewStep, s + 1))}
          >
            {step === reviewStep - 1 ? 'Review' : 'Next'}
          </button>
        )}
      </div>
      {isReview && totalAnswered === 0 && (
        <p
          id={nothingAnsweredHintId}
          style={{ fontSize: 13, color: 'var(--health-grey)', marginTop: 10, textAlign: 'right' }}
        >
          Nothing is compulsory, but a submission needs at least one answer.
        </p>
      )}
    </Shell>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

function Shell({
  children,
  churchName,
}: {
  children: ReactNode;
  churchName?: string;
}): JSX.Element {
  return (
    <div
      className="health-scope"
      style={{
        minHeight: '100vh',
        background: 'var(--health-paper, #fdfbf6)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PortalHeader
        portalName="Church Health Assessment"
        {...(churchName ? { toolName: churchName } : {})}
      />
      <main
        style={{
          width: '100%',
          maxWidth: 780,
          margin: '0 auto',
          padding: '24px 16px 56px',
          flex: 1,
        }}
      >
        {children}
      </main>
      <footer
        style={{
          borderTop: '1px solid #e5e0d5',
          background: '#fff',
          padding: '14px 0',
          textAlign: 'center',
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--health-grey, #6b7280)',
        }}
      >
        CCAANZ Church Health Assessment
      </footer>
    </div>
  );
}

function BookmarkNotice(): JSX.Element {
  return (
    <div className="health-banner" role="status" style={{ marginBottom: 16 }}>
      This is your own private link for this assessment. Bookmark this page so you can return to
      your answers later; without the link there is no other way back to them.
    </div>
  );
}

// ── Bot-check failure notice (cca-2s7tg) ──────────────────────────────────────

/** What happened, by why the widget stalled (TurnstileWidget.tsx). */
const BOT_CHECK_FAILED: Record<TurnstileFailure, string> = {
  load: 'The browser check could not load',
  challenge: 'The browser check could not finish in this browser',
  slow: 'The browser check is taking longer than it should',
};

/** UX-6: what happened, what to do next, and who can help. The remedy is the
 *  button, which remakes the widget and keeps the answers; never "reload".
 *  role="alert" as the forms engine's notice has (cca-sphc): it appears on its
 *  own, not after a press, and blocks the one thing the person came to do. A
 *  retry that fails again mounts it afresh, so it is announced again.
 *  `onFocusWithin` tells the page focus is inside it, `noticeRef` lets the page
 *  see focus is still inside it when its words change in place, and
 *  `retryRef` is where the page puts focus after a retry fails again
 *  (SurveyPage's focus effect). */
function BotCheckNotice({
  failure,
  onRetry,
  onFocusWithin,
  noticeRef,
  retryRef,
}: {
  failure: TurnstileFailure;
  onRetry: () => void;
  onFocusWithin: () => void;
  noticeRef: Ref<HTMLDivElement>;
  retryRef: Ref<HTMLButtonElement>;
}): JSX.Element {
  return (
    <div
      ref={noticeRef}
      className="health-banner health-banner-warn"
      role="alert"
      onFocus={onFocusWithin}
    >
      <p>
        {BOT_CHECK_FAILED[failure]}, so your answers cannot be submitted yet. They are still here,
        and trying again will not lose them.
        {failure === 'load' &&
          ' If you use an ad blocker or content blocker, please allow challenges.cloudflare.com first.'}
      </p>
      <button
        ref={retryRef}
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 10 }}
        onClick={onRetry}
      >
        Try the check again
      </button>
      <RegistrarContact className="mt-2" />
    </div>
  );
}

// ── Edit re-authentication gate (cycle edit_reauth='on') ──────────────────────

function EditLockGate({
  token,
  churchName,
  onUnlock,
  onCancel,
}: {
  token: string;
  churchName: string;
  onUnlock: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [requested, setRequested] = useState(false);
  const [sending, setSending] = useState(false);
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const request = (): void => {
    setSending(true);
    setError(null);
    healthApi
      .requestEditPin(token)
      .then(() => {
        setRequested(true);
        setInfo(
          'If an email is on file for you, a 6-digit code is on its way. It expires in 10 minutes.',
        );
      })
      .catch(() => setError('The code could not be sent just now. Please try again in a moment.'))
      .finally(() => setSending(false));
  };

  const verify = (): void => {
    if (!/^\d{6}$/.test(pin)) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    setVerifying(true);
    setError(null);
    healthApi
      .verifyEditPin(token, pin)
      .then(() => onUnlock())
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setError(
          code === 'too_many'
            ? 'Too many attempts. Please request a new code.'
            : code === 'expired'
              ? 'That code has expired. Please request a new one.'
              : 'That code was not correct. Please check it and try again.',
        );
      })
      .finally(() => setVerifying(false));
  };

  return (
    <div className="card">
      <h2 style={{ fontSize: 20, marginBottom: 10 }}>Make a change to your answers</h2>
      <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 14 }}>
        Your reflections for <strong>{churchName}</strong> have already been submitted. To keep them
        secure, this assessment asks for a one-time code before any change. We will email it to the
        address on file for you.
      </p>
      {info && (
        <div
          className="health-banner health-banner-blue"
          role="status"
          style={{ marginBottom: 12 }}
        >
          {info}
        </div>
      )}
      {error && (
        <p style={{ fontSize: 13, color: 'var(--band-priority)', marginBottom: 12 }}>{error}</p>
      )}
      {!requested ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" disabled={sending} onClick={request}>
            {sending ? 'Sending...' : 'Email me a code'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Not now
          </button>
        </div>
      ) : (
        <>
          <label
            className="health-input-label"
            htmlFor="edit-pin"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Enter the 6-digit code
          </label>
          <input
            id="edit-pin"
            className="health-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ maxWidth: 170, letterSpacing: '0.3em', fontSize: 18, textAlign: 'center' }}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={verifying || pin.length !== 6}
              onClick={verify}
            >
              {verifying ? 'Checking...' : 'Verify and edit'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={sending} onClick={request}>
              {sending ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Role picker (open mode) ───────────────────────────────────────────────────

function RolePicker({
  roles,
  value,
  onChange,
  heading,
}: {
  roles: string[];
  value: string | null;
  onChange: (role: string) => void;
  heading: string;
}): JSX.Element {
  const ordered = ROLE_ORDER.filter((r) => roles.includes(r));
  return (
    <fieldset
      style={{
        border: '1px solid #e5e0d5',
        borderRadius: 10,
        padding: '12px 16px',
        margin: '0 0 14px',
      }}
    >
      <legend style={{ fontSize: 13, fontWeight: 700, padding: '0 6px' }}>{heading}</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
        {ordered.map((r) => (
          <label
            key={r}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="respondent-role"
              value={r}
              checked={value === r}
              onChange={() => onChange(r)}
            />
            {roleLabel(r)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ── Review step ───────────────────────────────────────────────────────────────

function ReviewStep({
  sections,
  scores,
  texts,
  goTo,
  headingRef,
  firstRowRef,
  firstRowDescribedBy,
  rolePicker,
}: {
  sections: InstrumentSection[];
  scores: ScoreMap;
  texts: Record<string, string>;
  goTo: (step: number) => void;
  /** The step's heading: where focus goes on arriving at this step
   *  (SurveyPage's stepMoves effect, cca-iwefb). */
  headingRef: Ref<HTMLHeadingElement>;
  /** The first section row: where focus goes when the check passes but
   *  Submit stays disabled (SurveyPage's focus effect). */
  firstRowRef: Ref<HTMLButtonElement>;
  /** The id of the "needs at least one answer" hint while nothing is
   *  answered, so focus landing on the first row says why; else undefined. */
  firstRowDescribedBy: string | undefined;
  rolePicker: ReactNode;
}): JSX.Element {
  return (
    <div className="card">
      {/* Focused on arrival, so it takes focus only by script (tabIndex -1)
          and wears the module's focus ring (.health-step-heading). */}
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="health-step-heading"
        style={{ fontSize: 19, marginBottom: 4 }}
      >
        Review your answers
      </h2>
      <p style={{ fontSize: 13.5, color: 'var(--health-grey)', marginBottom: 16 }}>
        Nothing is compulsory. You can go back to any section, and you can return to this link to
        change your answers until the assessment closes.
      </p>
      {rolePicker}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {sections.map((section, i) => {
          const totalScores = section.scorecard.length;
          const answeredScores = section.scorecard.filter(
            (it) => typeof scores[it.id] === 'number',
          ).length;
          const answeredTexts = section.reflection.filter(
            (qn) => (texts[qn.id] ?? '').trim().length > 0,
          ).length;
          // Completeness signal for the scorecard only; reflections are
          // optional and never make a section look incomplete.
          const chip =
            totalScores > 0 && answeredScores === totalScores ? (
              <span className="pill pill-success">
                {'✓'} All {totalScores} answered
              </span>
            ) : answeredScores > 0 ? (
              <span className="pill pill-warn">
                {answeredScores} of {totalScores} answered
              </span>
            ) : totalScores > 0 ? (
              <span className="pill pill-neutral">Not answered yet</span>
            ) : null;
          return (
            <li key={section.no}>
              <button
                ref={i === 0 ? firstRowRef : undefined}
                aria-describedby={i === 0 ? firstRowDescribedBy : undefined}
                type="button"
                className="review-row"
                onClick={() => goTo(i)}
              >
                <span className="review-row-title">
                  {section.no}. {renderInstrumentText(section.title)}
                </span>
                <span className="review-row-meta">
                  {answeredTexts > 0 && (
                    <span className="review-note">
                      +{answeredTexts} {answeredTexts === 1 ? 'reflection' : 'reflections'}
                    </span>
                  )}
                  {chip}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Read-only view after close ────────────────────────────────────────────────

function ReadOnlyAnswers({
  sections,
  scores,
  texts,
  scale,
}: {
  sections: InstrumentSection[];
  scores: ScoreMap;
  texts: Record<string, string>;
  scale: Record<string, string>;
}): JSX.Element {
  return (
    <div>
      {sections.map((section) => {
        const { topReflection, followUps } = partitionFollowUps(section);
        const answeredFollowUps = (parentId: string) =>
          (followUps.get(parentId) ?? []).filter((fu) => (texts[fu.id] ?? '').trim().length > 0);
        const readOnlyText = (qn: { id: string; text: string }, indent: boolean) => (
          <div
            key={qn.id}
            style={
              indent
                ? { marginLeft: 18, paddingLeft: 14, borderLeft: '2px solid #e5e0d5' }
                : undefined
            }
          >
            <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 3 }}>
              {renderInstrumentText(qn.text)}
            </p>
            <p style={{ fontSize: 13, color: 'var(--health-grey)', whiteSpace: 'pre-wrap' }}>
              {(texts[qn.id] ?? '').trim()}
            </p>
          </div>
        );
        return (
          <div className="card" key={section.no}>
            <h2 style={{ fontSize: 17, marginBottom: 10 }}>
              {section.no}. {renderInstrumentText(section.title)}
            </h2>
            <div style={{ display: 'grid', gap: 12 }}>
              {section.scorecard.map((item) => {
                const value = scores[item.id];
                return (
                  <div key={item.id}>
                    <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 3 }}>
                      {renderInstrumentText(item.text)}
                    </p>
                    <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>
                      {typeof value === 'number'
                        ? `Your answer: ${value} (${scale[String(value)] ?? ''})`
                        : 'No answer given'}
                    </p>
                    {answeredFollowUps(item.id).map((fu) => readOnlyText(fu, true))}
                  </div>
                );
              })}
              {topReflection.map((qn) => {
                const answer = (texts[qn.id] ?? '').trim();
                const children = answeredFollowUps(qn.id);
                if (!answer && children.length === 0) return null;
                return (
                  <div key={qn.id}>
                    {answer ? readOnlyText(qn, false) : null}
                    {children.map((fu) => readOnlyText(fu, true))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
